import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, Eye as EyeIcon, MessageCircle, CalendarPlus, UserRound, History as HistoryIcon,
  StickyNote, CircleHelp, ContactRound, CalendarDays, Activity, Clock, RotateCcw,
  Phone, Mail, Plus, ShieldAlert, Pencil, Link2, ExternalLink, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn, initialsOf } from "@/lib/utils";
import { fmtDate, relDays } from "@/pages/Reperes";
import { CHANNEL_FR, STATE_FR, followUpLabel } from "@/pages/PrisesDeContact";
import { ObserveDialog, ContactDialog, SituationDialog, LinkDialog } from "@/components/reperes/ReperesDialogs";
import { ResponseDialog, FollowUpDialog, ScheduleDialog, SnoozeDialog, ConfirmDialog, MessageDialog } from "@/components/outreach/OutreachDialogs";

import { translateFr as tfr } from "@/i18n";
function Card({ title, Icon, action, children, testid }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 xl:p-3.5 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs xl:text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}{title}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ k, v, warn }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{k}</dt>
      <dd className={cn("text-right font-medium min-w-0", warn && "text-warning")}>{v}</dd>
    </div>
  );
}

export default function OutreachDetail({ a, tf, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dlg, setDlg] = useState(null);
  const [history, setHistory] = useState(null);

  const loadDetail = useCallback(async () => {
    try { setDetail((await api.get(`/prises-de-contact/${a.id}`)).data); } catch { /* row stays usable */ }
  }, [a.id]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    api.get("/dashboard/activity", { params: { player_id: a.id, limit: 20 } })
      .then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  }, [a.id, detail]);

  const d = detail?.athlete || a;
  const contacts = detail?.contacts || [];
  const notes = detail?.notes || [];
  const ex = detail?.last_exchange || detail?.first_message || null;
  const obs = detail?.next_observation || null;
  const state = d.contact_state;
  const refresh = () => { loadDetail(); onChanged(); };

  const run = async (path, body = {}) => {
    try {
      const r = await api.post(`/prises-de-contact/${a.id}/${path}`, body);
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); return null; }
  };

  // Contextual actions per sub-state (spec §15)
  const ACTIONS = {
    EN_ATTENTE: [
      { label: tfr("Voir le message envoyé"), primary: true, icon: EyeIcon, run: () => setDlg({ type: "message" }), testid: "action-view-message" },
      { label: tfr("Enregistrer une réponse"), icon: MessageCircle, run: () => setDlg({ type: "response" }), testid: "action-response" },
      { label: "Relancer maintenant", icon: Send, run: () => setDlg({ type: "followup" }), testid: "action-followup" },
    ],
    A_RELANCER: [
      { label: "Relancer", primary: true, icon: Send, run: () => setDlg({ type: "followup" }), testid: "action-followup" },
      { label: tfr("Réponse reçue"), icon: MessageCircle, run: () => setDlg({ type: "response" }), testid: "action-response" },
    ],
    REPONSE_RECUE: [
      { label: tfr("Traiter la réponse"), primary: true, icon: MessageCircle, run: () => setDlg({ type: "qualify" }), testid: "action-qualify" },
    ],
    SANS_REPONSE: [
      { label: "Remettre en veille", primary: true, icon: Clock, run: () => setDlg({ type: "snooze" }), testid: "action-snooze" },
      { label: "Relancer exceptionnellement", icon: Send, run: () => setDlg({ type: "reactivate-followup" }), testid: "action-exceptional-followup" },
    ],
    REFUS: [
      { label: "Consulter l'historique", primary: true, icon: HistoryIcon, run: () => setTab("history"), testid: "action-history" },
      { label: tfr("Réactiver"), icon: RotateCcw, run: () => setDlg({ type: "reactivate" }), testid: "action-reactivate" },
    ],
  }[state] || [];

  const fl = followUpLabel(d.next_follow_up_days, tf);
  const TABS = [
    { id: "overview", label: tfr("Vue d'ensemble"), Icon: CircleHelp },
    { id: "notes", label: tfr("Notes"), Icon: StickyNote, count: notes.length },
    { id: "history", label: tfr("Historique"), Icon: HistoryIcon },
  ];

  return (
    <div className="rounded-2xl border-0 bg-transparent p-0 xl:border xl:border-border xl:bg-background/40 xl:p-4 space-y-5 xl:space-y-4 min-w-0" data-testid="outreach-detail">
      <div className="flex items-start gap-3.5 xl:gap-3 min-w-0 pr-11 xl:pr-0">
        <Avatar className="h-16 w-16 xl:h-14 xl:w-14 shrink-0">
          <AvatarImage src={d.photo_url} alt="" />
          <AvatarFallback>{initialsOf(d.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[22px] leading-tight xl:text-lg font-bold truncate" data-testid="detail-name">{d.name}</span>
            {d.minor && <span className="text-[10px] font-semibold uppercase rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid="detail-minor-badge">{tf("Mineur")}</span>}
            <span className="text-[10px] rounded-full border border-border px-1.5 py-px text-muted-foreground" data-testid="detail-state-badge">{tf(STATE_FR[state] || "")}</span>
          </div>
          <div className="text-[15px] xl:text-xs text-muted-foreground truncate">
            {[d.age ? `${d.age} ans` : null, d.position, d.club || tf("Sans club")].filter(Boolean).join(" · ")}
          </div>
          <div className="text-[13px] xl:text-[11px] text-muted-foreground mt-1.5 xl:mt-1" data-testid="detail-first-contact">
            {tf("1er contact")} : {fmtDate(d.first_contact_at)}{d.first_contact_days_ago != null ? ` · il y a ${d.first_contact_days_ago} j` : ""}
            {d.first_contact_channel ? ` · ${CHANNEL_FR[d.first_contact_channel] || d.first_contact_channel}` : ""}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="detail-actions">
        {ACTIONS.map((ac) => (
          <Button key={ac.label} size="sm" onClick={ac.run} data-testid={ac.testid}
            variant={ac.primary ? "default" : "outline"}
            className={cn("text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3", ac.primary && "bg-foreground hover:bg-foreground/90 text-background")}>
            <ac.icon className="h-3.5 w-3.5 mr-1.5" />{tf(ac.label)}
          </Button>
        ))}
        <Button variant="ghost" size="sm"
          className="w-full xl:w-auto justify-start h-14 xl:h-9 text-sm xl:text-xs text-foreground xl:text-muted-foreground border-y border-border/60 xl:border-0 rounded-none xl:rounded-md px-1 xl:px-3"
          onClick={() => navigate(`/players/${a.id}`)} data-testid="detail-open-profile">
          <UserRound className="h-4 w-4 xl:h-3.5 xl:w-3.5 mr-2 xl:mr-1.5" />{tf("Profil complet")}
          <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground xl:hidden" aria-hidden="true" />
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto scroll-thin" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            data-testid={`detail-tab-${t.id}`}
            className={cn("shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm xl:px-3 xl:py-2 xl:text-xs font-medium border-b-2 -mb-px transition-colors",
              tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            <t.Icon className="h-3.5 w-3.5" />{tf(t.label)}
            {t.count > 0 && <span className="text-[10px] tabular-nums rounded-full border border-border px-1.5">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 min-[1700px]:grid-cols-2 gap-4" data-testid="detail-overview">
          <Card title={tf("Dernier échange")} Icon={MessageCircle} testid="card-last-exchange"
            action={ex && <button onClick={() => setDlg({ type: "message" })} className="text-[11px] text-info hover:underline" data-testid="view-full-message">{tf("Voir tout")}</button>}>
            {ex ? (
              <div className="text-sm xl:text-xs space-y-1" data-testid="last-exchange-content">
                <div className="font-semibold">{{ whatsapp: "Message WhatsApp", email: "Email envoyé", call: "Appel", meeting: "Rendez-vous", video: "Visio" }[ex.type] || tf("Échange")}</div>
                <div className="text-muted-foreground tabular-nums">{fmtDate(ex.occurred_at)} · {relDays(ex.occurred_at, tf)}</div>
                {ex.note && <p className="text-foreground/85 italic line-clamp-3 border-l-2 border-border pl-2 mt-1">« {ex.note} »</p>}
              </div>
            ) : <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun échange enregistré.")}</p>}
          </Card>

          <Card title={tf("Prochaine relance")} Icon={CalendarDays} testid="card-next-followup"
            action={!["REFUS", "SANS_REPONSE", "REPONSE_RECUE"].includes(state) &&
              <button onClick={() => setDlg({ type: "schedule" })} className="text-muted-foreground hover:text-foreground" aria-label={tf("Modifier la date")} data-testid="edit-schedule-btn"><Pencil className="h-3.5 w-3.5" /></button>}>
            {state === "REFUS" ? (
              <p className="text-sm xl:text-xs text-muted-foreground">{tf("Refus enregistré")}{d.refusal_reason ? ` — ${d.refusal_reason}` : ""}</p>
            ) : state === "SANS_REPONSE" ? (
              <p className="text-xs text-muted-foreground" data-testid="no-response-info">
                {tf("Séquence épuisée")} ({d.contact_attempts}/{d.max_attempts}).
                {d.contact_dormant_until ? ` ${tf("En veille jusqu'au")} ${fmtDate(d.contact_dormant_until)}.` : ""}
              </p>
            ) : d.next_follow_up_date ? (
              <div className="text-sm xl:text-xs space-y-1" data-testid="next-followup-content">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold tabular-nums">{fmtDate(d.next_follow_up_date)}</span>
                  <span className={cn("text-[11px] font-medium", fl?.tone === "overdue" ? "text-athlynx" : fl?.tone === "today" ? "text-warning" : "text-muted-foreground")}>{fl?.text}</span>
                </div>
                <div className="text-muted-foreground">{tf("Relance")} n°{Math.min(d.contact_attempts + 1, d.max_attempts)}/{d.max_attempts} · {tf("Automatique : J+5")}</div>
              </div>
            ) : <p className="text-sm xl:text-xs text-muted-foreground">—</p>}
          </Card>

          <Card title={tf("Interlocuteurs")} Icon={ContactRound} testid="card-interlocutors"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => setDlg({ type: "add-contact" })} data-testid="add-contact-btn"><Plus className="h-3 w-3 mr-1" />{tf("Ajouter")}</Button>}>
            {d.minor && !contacts.some((c) => c.is_legal_representative) && (
              <div className="rounded-lg border border-warning/30 bg-warning/[0.06] p-2 mb-2 text-[11px] text-warning flex items-center gap-1.5" data-testid="no-legal-rep-warning">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0" />{tf("Aucun représentant légal renseigné.")}
              </div>
            )}
            {contacts.length === 0 ? <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun interlocuteur renseigné.")}</p> : (
              <ul className="space-y-2.5">
                {contacts.map((c) => (
                  <li key={c.id} className="flex items-start justify-between gap-2 min-w-0" data-testid={`contact-${c.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold">{c.name}</span>
                        {c.is_legal_representative && <span className="text-[10px] font-semibold rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid={`legal-rep-badge-${c.id}`}>{tf("Représentant légal")}</span>}
                      </div>
                      {c.relationship && <div className="text-[11px] text-muted-foreground">{tf(c.relationship)}</div>}
                      {c.phone && <div className="text-[11px] text-muted-foreground tabular-nums">{c.phone}</div>}
                      {c.email && <div className="text-[11px] text-muted-foreground truncate">{c.email}</div>}
                      <div className="text-[10px] text-muted-foreground/70 mt-0.5">{tf("Source")} : {c.source || tf("non renseignée")}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {c.phone && <a href={`https://wa.me/${c.phone.replace(/[^0-9+]/g, "")}`} target="_blank" rel="noreferrer" className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60" aria-label={`WhatsApp ${c.name}`}><MessageCircle className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                      {c.phone && <a href={`tel:${c.phone}`} className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60" aria-label={`${tf("Appeler")} ${c.name}`}><Phone className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                      {c.email && <a href={`mailto:${c.email}`} className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60" aria-label={`Email ${c.name}`}><Mail className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title={tf("Situation actuelle")} Icon={Activity} testid="card-situation"
            action={<button onClick={() => setDlg({ type: "situation" })} className="text-muted-foreground hover:text-foreground" aria-label={tf("Modifier la situation")} data-testid="edit-situation-btn"><Pencil className="h-3.5 w-3.5" /></button>}>
            <dl className="space-y-2 text-sm xl:space-y-1.5 xl:text-xs">
              <Row k={tf("Club actuel")} v={d.club || tf("Sans club")} />
              <Row k={tf("Dernier club")} v={d.last_club || "—"} />
              <Row k={tf("Niveau")} v={d.level || "—"} />
              <Row k={tf("Fin de contrat")} warn={d.contract_days_left !== null && d.contract_days_left <= 180}
                v={d.contract_end_date ? (
                  <span data-testid="situation-contract">{fmtDate(d.contract_end_date)}
                    <span className="ml-1.5 inline-block text-[10px] rounded-full border border-border text-muted-foreground px-1.5 py-px">{tf(d.contract_remaining_label || "")}</span>
                  </span>) : tf("Non renseignée")} />
              <Row k={tf("Nationalité")} v={d.nationality || "—"} />
            </dl>
          </Card>

          <Card title={tf("Notes récentes")} Icon={StickyNote} testid="card-notes"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => setTab("notes")} data-testid="notes-add-btn"><Plus className="h-3 w-3 mr-1" />{tf("Ajouter")}</Button>}>
            {notes.length === 0 ? <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucune note pour le moment.")}</p> : (
              <ul className="space-y-2">
                {notes.slice(0, 2).map((n) => (
                  <li key={n.id} className="text-xs">
                    <p className="text-foreground/85">« {n.note} »</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{relDays(n.occurred_at, tf)}</p>
                  </li>
                ))}
              </ul>
            )}
            {notes.length > 2 && <button onClick={() => setTab("notes")} className="text-[11px] text-info hover:underline mt-2" data-testid="notes-see-all">{tf("Voir toutes les notes")} ({notes.length})</button>}
          </Card>

          <Card title={tf("Prochain match / Observation")} Icon={CalendarPlus} testid="card-observation">
            {obs ? (
              <div className="space-y-1 text-xs" data-testid="next-observation">
                <div className="font-semibold">{obs.title}</div>
                <div className="text-muted-foreground tabular-nums">{fmtDate(obs.starts_at)}{obs.location ? ` · ${obs.location}` : ""}</div>
              </div>
            ) : (
              <div className="text-sm xl:text-xs text-muted-foreground">
                {tf("Aucun prochain match renseigné")}
                <div className="pt-2">
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDlg({ type: "observe" })} data-testid="obs-plan-btn">
                    <CalendarPlus className="h-3 w-3 mr-1" />{tf("Planifier une observation")}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          <Card title={tf("Liens utiles")} Icon={Link2} testid="card-links"
            action={<Button variant="ghost" size="sm" className="h-7 text-[11px] px-2" onClick={() => setDlg({ type: "link" })} data-testid="add-link-btn"><Plus className="h-3 w-3 mr-1" />{tf("Ajouter")}</Button>}>
            {(d.links || []).length === 0 ? <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun lien ajouté.")}</p> : (
              <ul className="space-y-1">
                {d.links.map((l, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-info hover:underline min-w-0 truncate">
                      <ExternalLink className="h-3 w-3 shrink-0" /><span className="truncate">{l.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {tab === "notes" && <NotesTab a={a} tf={tf} notes={notes} onSaved={loadDetail} />}

      {tab === "history" && (
        <div className="space-y-2" data-testid="detail-history">
          {!history ? <div className="h-20 rounded-xl bg-secondary/60 animate-pulse" /> :
            history.length === 0 ? <p className="text-xs text-muted-foreground py-6 text-center">{tf("Aucun événement dans l'historique.")}</p> : (
              <ul className="space-y-2.5">
                {history.map((h) => (
                  <li key={h.id} className="flex items-start gap-2.5 text-xs">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-foreground/50 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground/90">{h.title}</p>
                      {h.subtitle && <p className="text-[11px] text-muted-foreground">{h.subtitle}</p>}
                      <p className="text-[10px] text-muted-foreground/70">{fmtDate(h.at)} · {relDays(h.at, tf)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}

      {dlg?.type === "message" && <MessageDialog ex={ex} tf={tf} onClose={() => setDlg(null)} />}
      {(dlg?.type === "response" || dlg?.type === "qualify") && (
        <ResponseDialog a={d} contacts={contacts} prefill={dlg.type === "qualify"} tf={tf}
          onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />
      )}
      {dlg?.type === "followup" && <FollowUpDialog a={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "reactivate-followup" && (
        <ConfirmDialog tf={tf} title={tf("Relancer exceptionnellement ?")}
          body={tf("La séquence est épuisée. Cette relance réinitialise un nouveau cycle de contact.")}
          onClose={() => setDlg(null)}
          onConfirm={async () => { const r = await run("reactivate"); if (r) { await run("follow-up", {}); toast.success(tf("Relance effectuée")); setDlg(null); refresh(); } }} />
      )}
      {dlg?.type === "snooze" && <SnoozeDialog a={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "reactivate" && (
        <ConfirmDialog tf={tf} title={tf("Réactiver ce contact ?")}
          body={tf("Un refus a été enregistré. La réactivation ouvre un nouveau cycle de prise de contact.")}
          onClose={() => setDlg(null)}
          onConfirm={async () => { const r = await run("reactivate"); if (r) { toast.success(tf("Contact réactivé")); setDlg(null); refresh(); } }} />
      )}
      {dlg?.type === "schedule" && <ScheduleDialog a={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "observe" && <ObserveDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "add-contact" && <ContactDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); loadDetail(); }} />}
      {dlg?.type === "situation" && <SituationDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "link" && <LinkDialog p={d} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
    </div>
  );
}

function NotesTab({ a, tf, notes, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/interactions", { player_id: a.id, type: "note", note: text.trim() });
      setText(""); toast.success(tf("Note ajoutée")); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="detail-notes-tab">
      <textarea value={text} onChange={(e) => setText(e.target.value)}
        placeholder={tf("Ajouter une note sur cet athlète…")}
        className="w-full rounded-lg border border-border bg-background text-xs min-h-[70px] p-2.5 focus:outline-none focus:ring-1 focus:ring-athlynx/50" data-testid="note-input" />
      <Button size="sm" disabled={busy || !text.trim()} onClick={save}
        className="bg-foreground hover:bg-foreground/90 text-background text-xs h-8" data-testid="note-save-btn">{tf("Enregistrer la note")}</Button>
      {notes.map((n) => (
        <div key={n.id} className="rounded-xl border border-border bg-card p-3 text-xs">
          <p className="text-foreground/90 whitespace-pre-wrap">{n.note}</p>
          <p className="text-[10px] text-muted-foreground mt-1.5">{fmtDate(n.occurred_at)} · {relDays(n.occurred_at, tf)}</p>
        </div>
      ))}
    </div>
  );
}
